import * as React from "react";
import { FileText } from "lucide-react";
import type { WatchNoteCite, WatchNoteDocument } from "@hp/api-client";
import { HoverHint } from "../HoverHint";
import { SourceDocumentViewer } from "../SourceDocumentViewer";
import {
  citeChipText,
  QUOTE_NOT_VERIFIED,
  viewerRequestForCite,
  viewerRequestForDocument,
  type ViewerRequest,
} from "./sourceHelpers";

type OpenFn = (request: ViewerRequest) => void;

const OpenContext = React.createContext<OpenFn | null>(null);

/**
 * Holds the one document viewer for every watch note inside it. One viewer
 * for the whole list (not one per card) so opening a source never changes the
 * height of a card; the viewer is a native modal <dialog> in the top layer, so
 * nothing on the page moves when it opens. A provider nested in another reuses
 * the outer one, so a card works both inside the list and on its own.
 */
export function WatchNoteSourcesProvider({ children }: { children: React.ReactNode }) {
  const parent = React.useContext(OpenContext);
  const [open, setOpen] = React.useState<ViewerRequest | null>(null);
  const close = React.useCallback(() => setOpen(null), []);
  if (parent) return <>{children}</>;
  return (
    <OpenContext.Provider value={setOpen}>
      {children}
      {open ? (
        <SourceDocumentViewer
          // A new key per request so switching straight from one cite to another starts the viewer fresh
          // (its highlight/whole-document mode is local state).
          key={`${open.source.id}-${open.passage?.page ?? open.startPage ?? ""}-${open.passage?.text ?? ""}`}
          source={open.source}
          passage={open.passage}
          startPage={open.startPage}
          onClose={close}
        />
      ) : null}
    </OpenContext.Provider>
  );
}

function useOpen(): OpenFn | null {
  return React.useContext(OpenContext);
}

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1";

/** Why a chip opens what it opens, for the hover hint and screen readers. */
function citeHint(cite: WatchNoteCite): string {
  if (!cite.document_id) return `${cite.label || cite.file_name}: not held yet, to fetch.`;
  const where = cite.page ? `page ${cite.page}` : "the first page";
  if (cite.quote && cite.quote_verified) return `Open ${cite.label || cite.file_name} at ${where} with the quoted words highlighted.`;
  if (cite.quote) return `Open ${cite.label || cite.file_name} at ${where} (${QUOTE_NOT_VERIFIED}).`;
  return `Open ${cite.label || cite.file_name} at ${where}.`;
}

/**
 * One source as a small chip: "¶90 · p43 · 11.07.2017". A held document is a
 * button that opens it at that page; a document not held is a muted "to fetch"
 * chip with its file name, so the reader sees what is missing.
 */
export function SourceChip({ cite }: { cite: WatchNoteCite }) {
  const open = useOpen();
  const request = viewerRequestForCite(cite);
  const hint = citeHint(cite);
  if (!request || !open) {
    return (
      <HoverHint hint={hint}>
        <span
          tabIndex={0}
          aria-label={hint}
          data-cite-state="to-fetch"
          className={`inline-flex max-w-[16rem] items-center gap-1 rounded border border-dashed border-zinc-300 bg-zinc-50 px-1.5 py-px text-[0.6875rem] text-zinc-400 ${FOCUS}`}
        >
          <span className="font-medium uppercase tracking-wide">to fetch</span>
          <span className="truncate">{cite.file_name || cite.label}</span>
        </span>
      </HoverHint>
    );
  }
  // An unverified quote is still worth opening at its page, but the chip is drawn
  // amber so the reader knows the highlight will not be there.
  const unverified = Boolean(cite.quote) && !cite.quote_verified;
  return (
    <HoverHint hint={hint}>
      <button
        type="button"
        aria-label={hint}
        data-cite-state={unverified ? "unverified" : "held"}
        onClick={(e) => {
          e.stopPropagation();
          open(request);
        }}
        className={[
          "inline-flex items-center gap-1 rounded border px-1.5 py-px text-[0.6875rem] tabular-nums transition-colors",
          unverified
            ? "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
            : "border-zinc-200 bg-white text-brand-700 hover:border-brand-300 hover:bg-brand-50",
          FOCUS,
        ].join(" ")}
      >
        <FileText className="h-3 w-3 flex-none" aria-hidden />
        {citeChipText(cite)}
      </button>
    </HoverHint>
  );
}

/** A row of chips after a fact; nothing when the fact has no source. */
export function SourceChips({ cites }: { cites: WatchNoteCite[] | undefined }) {
  if (!cites?.length) return null;
  return (
    <span className="ml-1 inline-flex flex-wrap gap-1 align-baseline" aria-label="Sources">
      {cites.map((cite, i) => (
        <SourceChip key={`${cite.document_id ?? cite.file_name}-${cite.page ?? ""}-${i}`} cite={cite} />
      ))}
    </span>
  );
}

/**
 * A document name as a link-styled button that opens it at page 1 (the note's
 * own document, or a file in the "checked, nothing found" log). Plain text when
 * there is no viewer to open it in.
 */
export function DocumentButton({ doc, children, className = "" }: { doc: WatchNoteDocument; children?: React.ReactNode; className?: string }) {
  const open = useOpen();
  const label = children ?? doc.file_name;
  if (!open) return <span className={className}>{label}</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        open(viewerRequestForDocument(doc));
      }}
      title={`Open ${doc.file_name}`}
      className={`inline max-w-full text-left text-brand-700 underline decoration-dotted underline-offset-2 hover:decoration-solid ${FOCUS} ${className}`}
    >
      {label}
    </button>
  );
}

/** A HELD badge that opens the order at its cited page (a TO FETCH badge is drawn by the caller). */
export function HeldBadgeButton({ cite }: { cite: WatchNoteCite }) {
  const open = useOpen();
  const request = viewerRequestForCite(cite);
  const hint = citeHint(cite);
  const className = "rounded bg-zinc-100 px-1 py-px text-[0.625rem] font-medium uppercase tracking-wide text-zinc-600";
  if (!request || !open) return <span className={className}>held</span>;
  return (
    <HoverHint hint={hint}>
      <button
        type="button"
        aria-label={hint}
        onClick={(e) => {
          e.stopPropagation();
          open(request);
        }}
        className={`${className} inline-flex items-center gap-0.5 hover:bg-brand-50 hover:text-brand-700 ${FOCUS}`}
      >
        <FileText className="h-2.5 w-2.5" aria-hidden />
        held
      </button>
    </HoverHint>
  );
}
